import type { Permission, SessionUser } from '@playplate/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, SESSION_EXPIRED_EVENT } from '../lib/api';
import { realtime } from '../lib/realtime';

type AuthState = {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<SessionUser>('/auth/me');
      setUser(me);
    } catch (e) {
      // A 401 here is the normal "not signed in yet" case on first load, not an
      // error worth showing anyone.
      if (!(e instanceof ApiError) || e.status !== 401) {
        console.error('Failed to load session', e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The API client raises this whenever any request comes back 401 — a session
  // that expired mid-shift, or an account deactivated by an admin. Clearing here
  // means every screen reacts, not just the one that happened to make the call.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      realtime.disconnect();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // The socket's lifetime is tied to the session, not to any screen: navigating
  // between pages must not drop and re-establish it.
  useEffect(() => {
    if (user) realtime.connect();
    else realtime.disconnect();
    return () => realtime.disconnect();
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await api.post<SessionUser>('/auth/login', { email, password }, { suppressAuthRedirect: true });
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      realtime.disconnect();
    }
  }, []);

  const can = useCallback(
    (permission: Permission) => Boolean(user?.permissions.includes(permission)),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, refresh, can }),
    [user, loading, login, logout, refresh, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
